import update from 'immutability-helper';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker-cssmodules.css';
import 'react-datepicker/dist/react-datepicker.css';
import {
    FormattedMessage,
    WrappedComponentProps,
    injectIntl,
    useIntl,
} from 'react-intl';
import { connect } from 'react-redux';
import { RootState } from '../../../store';
import { IAccount } from '../../../models/company-account';
import {
    IOrderHistoryLine,
    IOrderHistoryRequest,
    IOrderHistoryResult,
} from '../../../models/order';
import { ISession } from '../../../models/pro-sites';
import * as accountActions from '../../../store/account/account-actions';
import {
    loadOrders,
    setOrderHistoryRequest,
} from '../../../store/order/order-actions';
import '../../common/react-datepicker.css';
import SelectInputSimple from '../../inputs/select-input-simple';
import OrderHistoryList from './order-history-list';
import './order-history.css';
import { setIsBusy } from '../../../store/api/api-status-action';
import OverLimitModal from './over-limit-modal';
import { Constants } from '../../../utils/constants';
import { hasFeature } from '../../authorization/features';
import { EXPECTED_SHIP_DATE } from '../../../models/feature-constants';
interface IOrderHistoryProps {
    shipToAccounts: IAccount[];
    billToAccounts: IAccount[];
    orderHistory: IOrderHistoryResult | null;
    orders: IOrderHistoryLine[];
    session: ISession | null;
    loadShipToAccounts: () => Promise<void>;
    loadBillToAccounts: () => Promise<void>;
    loadOrders: (request: IOrderHistoryRequest) => Promise<void>;
    setIsBusy: (isBusy: boolean) => Promise<void>;
    orderHistoryRequest: IOrderHistoryRequest | null;
    setOrderHistoryRequest: (result: IOrderHistoryRequest) => {
        result: IOrderHistoryRequest | null;
    };
}

export const OrderHistory = ({
    shipToAccounts,
    billToAccounts,
    orderHistory,
    orders,
    session,
    loadShipToAccounts,
    loadOrders,
    setIsBusy,
    loadBillToAccounts,
    orderHistoryRequest,
    setOrderHistoryRequest,
}: IOrderHistoryProps) => {
    const intl = useIntl();
    const [overLimit, setOverLimit] = useState(false);
    const defaultTimeFrameIdx = 0;
    const customTimeFrameIdx = 4;
    const timeFrameData: {
        value: number;
        text: string;
    }[] = [
        { value: 30, text: 'View_Last_30_Days' },
        { value: 60, text: 'View_Last_60_Days' },
        { value: 90, text: 'View_Last_90_Days' },
        { value: 100, text: 'Year_To_Date' },
        { value: 110, text: 'Custom_Date_Range' },
    ];
    const initialRequest: IOrderHistoryRequest = {
        shipToAccountNumber: session!.isAccountThirdParty
            ? null
            : session!.account,
        billToAccountNumber: session!.isAccountThirdParty
            ? session!.billingAccount
            : null,
        timeFrameValue: timeFrameData[defaultTimeFrameIdx].value,
    };
    const [isDismissed, setIsDismissed] = useState(false);
    useEffect(() => {
        if (orders.length === 0) {
            loadOrders(initialRequest);
            localStorage.setItem('orderHistoryExceed', 'true');
            setOrderHistoryRequest(initialRequest);
        }

        if (shipToAccounts.length === 0 && !session!.isAccountThirdParty) {
            loadShipToAccounts();
        }

        if (session!.isAccountThirdParty) {
            loadBillToAccounts();
        }
    }, []);

    useEffect(() => {
        if (orderHistory && orderHistory.exceedsMaxOrderCount) {
            setOverLimit(true);
        }
    }, [orderHistory]);

    const getShipToAccountDisplay = (account: IAccount) => {
        let value = '';
        if (account.partyName) {
            value = account.partyName;
        }
        if (account.fullAccountNumber) {
            if (value.length > 0) {
                value += ' ';
            }
            value += '(' + account.fullAccountNumber + ')';
        }
        if (
            account.street1 &&
            account.city &&
            account.state &&
            account.postalCode
        ) {
            if (value.length > 0) {
                value += ' - ';
            }
            value +=
                account.street1 +
                ', ' +
                account.city +
                ', ' +
                account.state +
                ' ' +
                account.postalCode;
        }
        return value;
    };

    const handleChangeBase = (name: string, value: any) => {
        const request = update(orderHistoryRequest!, {
            [name]: { $set: value },
        });
        if (name === 'timeFrameValue') {
            // Set the custom date range to null when it is no longer a custom date range
            delete request.timeFrameCustomFromDate;
            delete request.timeFrameCustomToDate;
        }
        if (
            name === Constants.SHIP_TO_ACCOUNT_NUMBER ||
            name === Constants.BILL_TO_ACCOUNT_NUMBER ||
            (name === 'timeFrameValue' &&
                value !== timeFrameData[customTimeFrameIdx].value.toString()) ||
            (name.startsWith('timeFrameCustom') &&
                request.timeFrameCustomFromDate)
        ) {
            // Load orders when the ship to dropdown changes OR
            // the time frame dropdown changes for all but custom date range OR
            // custom date range is selected along with the from date
            loadOrders(request);
        }
        setOrderHistoryRequest(request);
    };

    const handleChange = (event: any) => {
        const { name, value } = event.target;
        handleChangeBase(name, value);
    };

    const handleFromDatePickerChange = (date: Date) => {
        handleChangeBase('timeFrameCustomFromDate', date);
    };

    const handleToDatePickerChange = (date: Date) => {
        handleChangeBase('timeFrameCustomToDate', date);
    };

    return (
        <>
            {shipToAccounts && orders && session && orderHistoryRequest && (
                <div id="wrapper" style={{ paddingBottom: '80px' }}>
                    <div className="row">
                        <div
                            className={
                                session.isAccountThirdParty ? 'col-md-6' : ''
                            }
                        >
                            <h1>
                                <FormattedMessage id="Order_History" />
                            </h1>
                        </div>
                        {session.isAccountThirdParty && (
                            <div
                                className="col-md-6"
                                style={{
                                    position: 'relative',
                                }}
                            >
                                <a href="/">
                                    <img
                                        id="header-logo"
                                        style={{
                                            position: 'absolute',
                                            top: '-36px',
                                            right: '27px',
                                        }}
                                        src="/images/branding/header-logo.png"
                                        alt="header-logo"
                                    />
                                </a>
                            </div>
                        )}
                    </div>

                    <div className="row" style={{ paddingTop: '20px' }}>
                        <div className="form-group col-md-12">
                            <div className="col-md-1">
                                <label
                                    className="control-label"
                                    style={{
                                        paddingTop: '8px',
                                    }}
                                    htmlFor={Constants.SHIP_TO_ACCOUNT_NUMBER}
                                >
                                    <FormattedMessage id="Location" />:
                                </label>
                            </div>
                            {session.isAccountThirdParty ? (
                                <div className="col-md-11">
                                    <SelectInputSimple
                                        name={Constants.BILL_TO_ACCOUNT_NUMBER}
                                        options={billToAccounts.map(
                                            (account) => ({
                                                value: account.fullAccountNumber,
                                                text: getShipToAccountDisplay(
                                                    account
                                                ),
                                            })
                                        )}
                                        value={
                                            orderHistoryRequest.billToAccountNumber!
                                        }
                                        onChange={handleChange}
                                    />
                                </div>
                            ) : (
                                <div className="col-md-11">
                                    <SelectInputSimple
                                        name={Constants.SHIP_TO_ACCOUNT_NUMBER}
                                        options={shipToAccounts.map(
                                            (account) => ({
                                                value: account.fullAccountNumber,
                                                text: getShipToAccountDisplay(
                                                    account
                                                ),
                                            })
                                        )}
                                        value={
                                            orderHistoryRequest.shipToAccountNumber!
                                        }
                                        onChange={handleChange}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <div
                        className="row"
                        style={{
                            padding: '20px 0px 20px 0px',
                        }}
                    >
                        <div className="form-group col-md-4">
                            <SelectInputSimple
                                name="timeFrameValue"
                                options={timeFrameData.map((timeFrame) => ({
                                    value: timeFrame.value,
                                    text: intl.formatMessage({
                                        id: timeFrame.text,
                                    }),
                                }))}
                                value={orderHistoryRequest.timeFrameValue}
                                onChange={handleChange}
                            />
                        </div>
                        {orderHistoryRequest.timeFrameValue.toString() ===
                            timeFrameData[
                                customTimeFrameIdx
                            ].value.toString() && (
                            <>
                                <div className="form-group col-md-4">
                                    <label
                                        className="control-label"
                                        style={{
                                            paddingRight: '20px',
                                        }}
                                        htmlFor="timeFrameCustomFromDate"
                                    >
                                        <FormattedMessage id="Start_Date" />:
                                    </label>
                                    <DatePicker
                                        selected={
                                            orderHistoryRequest.timeFrameCustomFromDate
                                        }
                                        selectsStart={true}
                                        startDate={
                                            orderHistoryRequest.timeFrameCustomFromDate
                                        }
                                        endDate={
                                            orderHistoryRequest.timeFrameCustomToDate
                                        }
                                        name="timeFrameCustomFromDate"
                                        onChange={handleFromDatePickerChange}
                                        minDate={
                                            orderHistoryRequest.timeFrameCustomToDate
                                                ? moment(
                                                      orderHistoryRequest.timeFrameCustomToDate
                                                  )
                                                      .subtract(365, 'day')
                                                      .toDate()
                                                : null
                                        }
                                        maxDate={
                                            orderHistoryRequest.timeFrameCustomToDate
                                        }
                                        className="form-control"
                                        autoComplete="off"
                                    />
                                </div>
                                <div className="form-group col-md-4">
                                    <label
                                        className="control-label"
                                        style={{
                                            paddingRight: '20px',
                                        }}
                                        htmlFor="timeFrameCustomToDate"
                                    >
                                        <FormattedMessage id="End_Date" />:
                                    </label>
                                    <DatePicker
                                        selected={
                                            orderHistoryRequest.timeFrameCustomToDate
                                        }
                                        selectsEnd={true}
                                        startDate={
                                            orderHistoryRequest.timeFrameCustomFromDate
                                        }
                                        endDate={
                                            orderHistoryRequest.timeFrameCustomToDate
                                        }
                                        name="timeFrameCustomToDate"
                                        onChange={handleToDatePickerChange}
                                        minDate={
                                            orderHistoryRequest.timeFrameCustomFromDate
                                        }
                                        maxDate={
                                            orderHistoryRequest.timeFrameCustomFromDate
                                                ? moment(
                                                      orderHistoryRequest.timeFrameCustomFromDate
                                                  )
                                                      .add(365, 'day')
                                                      .toDate()
                                                : null
                                        }
                                        className="form-control"
                                        autoComplete="off"
                                    />
                                </div>
                            </>
                        )}
                        <div className="row">
                            <div
                                className="col-md-12"
                                style={{
                                    textAlign: 'right',
                                    paddingRight: '28px',
                                }}
                            >
                                {/*
                                hasFeature(session!, EXPECTED_SHIP_DATE) && (
                                    <strong>
                                        <FormattedMessage
                                            id="Expected_Ship_Date_Unavailable"
                                            defaultMessage="Expected Ship Date will be unavailable as we
                                    make enhancements"
                                        />
                                </strong>)*/}
                            </div>
                        </div>
                    </div>
                    <OrderHistoryList orders={orders} session={session} />
                    {overLimit &&
                        orderHistory &&
                        localStorage.getItem('orderHistoryExceed') &&
                        !isDismissed && (
                            <OverLimitModal
                                orderHistory={orderHistory!}
                                onClose={() => {
                                    localStorage.removeItem(
                                        'orderHistoryExceed'
                                    );
                                    setIsDismissed(true);
                                }}
                                onAccept={() => undefined}
                            />
                        )}
                </div>
            )}
        </>
    );
};

const mapStateToProps = (state: RootState) => {
    return {
        shipToAccounts: state.shipToAccounts ? state.shipToAccounts.data! : [],
        orderHistory: state.orderHistory ? state.orderHistory.data! : null,
        orders: state.orderHistory ? state.orderHistory.data!.orders : [],
        session: state.session ? state.session.data! : null,
        billToAccounts: state.billToAccounts ? state.billToAccounts.data! : [],
        orderHistoryRequest: state.orderHistoryRequest
            ? state.orderHistoryRequest
            : null,
    };
};

const mapDispatchToProps = {
    loadShipToAccounts: accountActions.loadShipToAccounts,
    loadOrders,
    setIsBusy,
    loadBillToAccounts: accountActions.loadBillToAccounts,
    setOrderHistoryRequest,
};

const orderHistoryContainer = connect(
    mapStateToProps,
    mapDispatchToProps
)(OrderHistory);

export default orderHistoryContainer;
